import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    ArrowLeft,
    User,
    Tag,
    Clock,
    MessageSquare,
    CheckCircle,
    XCircle,
    Play,
    ExternalLink,
} from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import Textarea from '../../components/Textarea';
import Select from '../../components/Select';
import Input from '../../components/Input';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAdmin } from '../../contexts/AdminContext';
import {
    getSupportTicketById,
    updateSupportTicket,
    resolveSupportTicket,
    closeSupportTicket,
    getSupportTicketTags,
} from '../../utils/api';
import { formatDate, formatDateTime, formatTimeAgo } from '../../utils/helpers';
import toast from 'react-hot-toast';

const getStatusBadgeVariant = (status) => {
    switch (status) {
        case 'open': return 'warning';
        case 'in_progress': return 'info';
        case 'resolved': return 'success';
        case 'closed': return 'primary';
        default: return 'primary';
    }
};

const getPriorityBadgeVariant = (priority) => {
    switch (priority) {
        case 'high': return 'danger';
        case 'medium': return 'warning';
        case 'low': return 'info';
        default: return 'primary';
    }
};

const getStatusLabel = (status) => {
    switch (status) {
        case 'open': return 'Open';
        case 'in_progress': return 'In Progress';
        case 'resolved': return 'Resolved';
        case 'closed': return 'Closed';
        default: return status;
    }
};

export default function TicketDetail() {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { admin } = useAdmin();

    // Modal states
    const [showResolveModal, setShowResolveModal] = useState(false);
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [showEditTagsModal, setShowEditTagsModal] = useState(false);

    // Form states
    const [resolutionMessage, setResolutionMessage] = useState('');
    const [closeNote, setCloseNote] = useState('');
    const [selectedTags, setSelectedTags] = useState([]);

    // Fetch ticket
    const { data: ticketData, isLoading } = useQuery({
        queryKey: ['support-ticket', ticketId],
        queryFn: () => getSupportTicketById(ticketId),
    });

    // Fetch tags
    const { data: tagsData } = useQuery({
        queryKey: ['support-ticket-tags'],
        queryFn: getSupportTicketTags,
    });

    const ticket = ticketData?.data;
    const tags = tagsData?.data || [];

    // Update status mutation
    const updateStatusMutation = useMutation({
        mutationFn: ({ status, note }) => updateSupportTicket(ticketId, { status, note }),
        onSuccess: () => {
            toast.success('Ticket status updated');
            queryClient.invalidateQueries(['support-ticket', ticketId]);
        },
        onError: (error) => toast.error(error.message),
    });

    // Resolve mutation
    const resolveMutation = useMutation({
        mutationFn: () => resolveSupportTicket(ticketId, { resolutionMessage }),
        onSuccess: () => {
            toast.success('Ticket resolved and message sent to user');
            queryClient.invalidateQueries(['support-ticket', ticketId]);
            setShowResolveModal(false);
            setResolutionMessage('');
        },
        onError: (error) => toast.error(error.message),
    });

    // Close mutation
    const closeMutation = useMutation({
        mutationFn: () => closeSupportTicket(ticketId, { note: closeNote }),
        onSuccess: () => {
            toast.success('Ticket closed');
            queryClient.invalidateQueries(['support-ticket', ticketId]);
            setShowCloseModal(false);
            setCloseNote('');
        },
        onError: (error) => toast.error(error.message),
    });

    // Update tags mutation
    const updateTagsMutation = useMutation({
        mutationFn: () => updateSupportTicket(ticketId, { tags: selectedTags }),
        onSuccess: () => {
            toast.success('Tags updated');
            queryClient.invalidateQueries(['support-ticket', ticketId]);
            setShowEditTagsModal(false);
        },
        onError: (error) => toast.error(error.message),
    });

    const handleOpenEditTags = () => {
        setSelectedTags(ticket?.tags || []);
        setShowEditTagsModal(true);
    };

    const toggleTag = (tagName) => {
        setSelectedTags((prev) =>
            prev.includes(tagName)
                ? prev.filter(t => t !== tagName)
                : [...prev, tagName]
        );
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="large" />
            </div>
        );
    }

    if (!ticket) {
        return (
            <div className="text-center py-12">
                <h2 className="text-2xl font-bold mb-2">Ticket not found</h2>
                <Button onClick={() => navigate('/support')}>Back to Tickets</Button>
            </div>
        );
    }

    const canResolve = ticket.status !== 'resolved' && ticket.status !== 'closed';
    const canClose = ticket.status !== 'closed';

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="secondary" onClick={() => navigate('/support')}>
                        <ArrowLeft size={20} />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold">Ticket Details</h1>
                        <p className="text-muted-light dark:text-muted-dark">
                            View and manage this support ticket
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    {ticket.status === 'open' && (
                        <Button
                            variant="secondary"
                            onClick={() => updateStatusMutation.mutate({ status: 'in_progress' })}
                            loading={updateStatusMutation.isPending}
                        >
                            <Play size={18} />
                            Start Working
                        </Button>
                    )}
                    {canResolve && (
                        <Button
                            variant="success"
                            onClick={() => setShowResolveModal(true)}
                        >
                            <CheckCircle size={18} />
                            Resolve
                        </Button>
                    )}
                    {canClose && (
                        <Button
                            variant="danger"
                            onClick={() => setShowCloseModal(true)}
                        >
                            <XCircle size={18} />
                            Close
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Ticket Details */}
                    <Card>
                        <div className="flex items-start justify-between mb-4">
                            <h2 className="text-xl font-bold">{ticket.subject}</h2>
                            <Badge variant={getStatusBadgeVariant(ticket.status)}>
                                {getStatusLabel(ticket.status)}
                            </Badge>
                        </div>
                        <p className="text-foreground-light dark:text-foreground-dark whitespace-pre-wrap">
                            {ticket.description}
                        </p>
                    </Card>

                    {/* Resolution Message */}
                    {ticket.resolutionMessage && (
                        <Card title="Resolution Message">
                            <div className="p-4 bg-green/10 border border-green/20 rounded-lg">
                                <p className="whitespace-pre-wrap">{ticket.resolutionMessage}</p>
                                <div className="mt-3 pt-3 border-t border-green/20 text-sm text-muted-light dark:text-muted-dark">
                                    <p>Resolved by: {ticket.resolvedByEmail}</p>
                                    <p>Resolved at: {formatDateTime(ticket.resolvedAt)}</p>
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Status History */}
                    <Card title="Status History">
                        <div className="space-y-3">
                            {ticket.statusHistory?.map((entry, index) => (
                                <div
                                    key={index}
                                    className="flex items-start gap-3 p-3 bg-accent-light dark:bg-accent-dark rounded-lg"
                                >
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <Badge variant={getStatusBadgeVariant(entry.status)}>
                                                {getStatusLabel(entry.status)}
                                            </Badge>
                                            <span className="text-sm text-muted-light dark:text-muted-dark">
                                                by {entry.changedByEmail}
                                            </span>
                                        </div>
                                        {entry.note && (
                                            <p className="text-sm mt-1">{entry.note}</p>
                                        )}
                                    </div>
                                    <span className="text-xs text-muted-light dark:text-muted-dark">
                                        {formatTimeAgo(entry.changedAt)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* User Info */}
                    <Card title="User">
                        {ticket.targetUser ? (
                            <div className="flex items-center gap-3">
                                {ticket.targetUser.profilePicture ? (
                                    <img
                                        src={ticket.targetUser.profilePicture}
                                        alt=""
                                        className="w-12 h-12 rounded-full object-cover"
                                    />
                                ) : (
                                    <div className="w-12 h-12 rounded-full bg-accent-light dark:bg-accent-dark flex items-center justify-center">
                                        <span className="text-lg font-bold">
                                            {ticket.targetUser.username?.charAt(0)?.toUpperCase()}
                                        </span>
                                    </div>
                                )}
                                <div>
                                    <p className="font-medium">@{ticket.targetUser.username}</p>
                                    <p className="text-sm text-muted-light dark:text-muted-dark">
                                        {ticket.targetUser.displayName}
                                    </p>
                                </div>
                                <Button
                                    variant="secondary"
                                    size="small"
                                    onClick={() => navigate(`/users/${ticket.targetUserId}`)}
                                    className="ml-auto"
                                >
                                    <ExternalLink size={16} />
                                </Button>
                            </div>
                        ) : (
                            <p className="text-muted-light dark:text-muted-dark">User not found</p>
                        )}
                    </Card>

                    {/* Ticket Info */}
                    <Card title="Details">
                        <div className="space-y-3">
                            <div>
                                <p className="text-sm text-muted-light dark:text-muted-dark">Priority</p>
                                <Badge variant={getPriorityBadgeVariant(ticket.priority)}>
                                    {ticket.priority || 'Medium'}
                                </Badge>
                            </div>
                            <div>
                                <p className="text-sm text-muted-light dark:text-muted-dark">Source</p>
                                <p className="font-medium capitalize">
                                    {ticket.createdFrom === 'mobile' ? '📱 Mobile' : '🖥️ Admin Panel'}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-light dark:text-muted-dark">Created By</p>
                                <p className="font-medium">{ticket.createdByEmail}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-light dark:text-muted-dark">Created At</p>
                                <p className="font-medium">{formatDateTime(ticket.createdAt)}</p>
                            </div>
                            {ticket.assignedTo && (
                                <div>
                                    <p className="text-sm text-muted-light dark:text-muted-dark">Assigned To</p>
                                    <p className="font-medium">{ticket.assignedTo}</p>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* Tags */}
                    <Card
                        title="Tags"
                        action={
                            <Button variant="secondary" size="small" onClick={handleOpenEditTags}>
                                Edit
                            </Button>
                        }
                    >
                        <div className="flex flex-wrap gap-2">
                            {ticket.tags?.length > 0 ? (
                                ticket.tags.map((tagName) => {
                                    const tagInfo = tags.find(t => t.name === tagName);
                                    return (
                                        <span
                                            key={tagName}
                                            className="px-3 py-1 rounded-full text-sm"
                                            style={{
                                                backgroundColor: tagInfo?.color ? `${tagInfo.color}20` : '#6366f120',
                                                color: tagInfo?.color || '#6366f1'
                                            }}
                                        >
                                            {tagInfo?.label || tagName}
                                        </span>
                                    );
                                })
                            ) : (
                                <p className="text-sm text-muted-light dark:text-muted-dark">No tags</p>
                            )}
                        </div>
                    </Card>

                    {/* Conversation Link */}
                    {ticket.conversationId && (
                        <Card title="Linked Conversation">
                            <p className="text-sm text-muted-light dark:text-muted-dark mb-2">
                                This ticket was created from a chat conversation.
                            </p>
                            <p className="text-xs font-mono bg-accent-light dark:bg-accent-dark p-2 rounded">
                                {ticket.conversationId}
                            </p>
                        </Card>
                    )}
                </div>
            </div>

            {/* Resolve Modal */}
            <Modal
                isOpen={showResolveModal}
                onClose={() => setShowResolveModal(false)}
                title="Resolve Ticket"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowResolveModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="success"
                            onClick={() => resolveMutation.mutate()}
                            loading={resolveMutation.isPending}
                            disabled={!resolutionMessage.trim()}
                        >
                            <CheckCircle size={18} />
                            Resolve & Send Message
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <p className="text-sm text-muted-light dark:text-muted-dark">
                        Write a resolution message that will be sent to the user via the LBA account.
                    </p>
                    <Textarea
                        label="Resolution Message"
                        value={resolutionMessage}
                        onChange={(e) => setResolutionMessage(e.target.value)}
                        placeholder="Explain how the issue was resolved..."
                        rows={5}
                        required
                    />
                </div>
            </Modal>

            {/* Close Modal */}
            <Modal
                isOpen={showCloseModal}
                onClose={() => setShowCloseModal(false)}
                title="Close Ticket"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowCloseModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => closeMutation.mutate()}
                            loading={closeMutation.isPending}
                        >
                            <XCircle size={18} />
                            Close Ticket
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <p className="text-sm text-muted-light dark:text-muted-dark">
                        Close this ticket without sending a resolution message. Use this for tickets that don't require user communication.
                    </p>
                    <Textarea
                        label="Close Note (optional)"
                        value={closeNote}
                        onChange={(e) => setCloseNote(e.target.value)}
                        placeholder="Internal note about why the ticket is being closed..."
                        rows={3}
                    />
                </div>
            </Modal>

            {/* Edit Tags Modal */}
            <Modal
                isOpen={showEditTagsModal}
                onClose={() => setShowEditTagsModal(false)}
                title="Edit Tags"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowEditTagsModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => updateTagsMutation.mutate()}
                            loading={updateTagsMutation.isPending}
                        >
                            Save Tags
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <p className="text-sm text-muted-light dark:text-muted-dark">
                        Select one or more tags for this ticket.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {tags.map((tag) => (
                            <button
                                key={tag.name}
                                onClick={() => toggleTag(tag.name)}
                                className={`px-3 py-1.5 rounded-full text-sm transition-all ${selectedTags.includes(tag.name)
                                        ? 'ring-2 ring-offset-2 ring-blue'
                                        : 'opacity-60 hover:opacity-100'
                                    }`}
                                style={{
                                    backgroundColor: `${tag.color}20`,
                                    color: tag.color
                                }}
                            >
                                {tag.label}
                            </button>
                        ))}
                    </div>
                </div>
            </Modal>
        </div>
    );
}

